import { t } from '@lingui/core/macro';
import { Trans } from '@lingui/react/macro';
import dayjs from 'dayjs';
import { useMemo } from 'react';

import type { OpportunityType } from '@/api/opportunity';
import { RangeFilter } from '@/components/filters';
import {
  InstantSearchDataTable,
  type InstantSearchDataTableColumnDef,
} from '@/components/InstantSearch';
import { Link } from '@/components/typography';
import { USDateViewFormat } from '@/constants/formats';
import type SolAPIError from '@/error/SolAPIError';
import { CompanyFilter, OpportunityStatusFilter } from '@/features/filters';
import { OpportunityStatusTag } from '@/features/tags';
import { numberFormat } from '@/helper/numberFormatter';
import { ROUTES } from '@/router';

import OpportunityDomainCell from './OpportunityDomainCell';
import {
  type ALL_OPPORTUNITY_ACTIVITY_FIELDS,
  getOpportunityActivityFieldName,
} from './opportunityOverviewFields';

type Props = {
  data?: OpportunityType[];
  totalResults?: number;
  isLoading?: boolean;
  error: SolAPIError | null;
};

export const OpportunitiesOverviewTable = ({ data, totalResults, isLoading, error }: Props) => {
  const sortFields = ROUTES.opportunities.searchParams['o.sortBy'].enumValues;

  const columns = useMemo<InstantSearchDataTableColumnDef<OpportunityType>[]>(
    () => [
      {
        header: t`Company Name`,
        id: 'companyId',
        accessorKey: 'companyId',
        enableSorting: sortFields.includes('companyId'),
        filterComponent: (props) => <CompanyFilter {...props} />,
        minSize: 160,
        flex: 1,
        cell: ({ row }) => {
          const record = row.original;
          return record.account.company?.name ?? record.account.name ?? '-';
        },
      },
      {
        header: t`Opportunity`,
        id: 'name',
        accessorKey: 'name',
        enableSorting: sortFields.includes('name'),
        minSize: 160,
        flex: 1,
        cell: ({ row }) => (
          <Link variant="caption1" to={ROUTES.opportunityById(row.original.id)}>
            {row.original.name}
          </Link>
        ),
      },
      {
        header: t`Domain`,
        id: 'tld',
        accessorKey: 'tld',
        enableSorting: sortFields.includes('tld'),
        minSize: 120,
        flex: 1,
        cell: ({ row }) => <OpportunityDomainCell opportunity={row.original} />,
      },
      {
        header: t`Open Date`,
        id: 'openDate',
        accessorKey: 'openDate',
        meta: { align: 'end' },
        enableSorting: sortFields.includes('openDate'),
        size: 120,
        cell: ({ row }) => dayjs(row.original.openDate).format(USDateViewFormat),
      },
      {
        header: t`Close Date`,
        id: 'closeDate',
        accessorKey: 'closeDate',
        meta: { align: 'end' },
        enableSorting: sortFields.includes('closeDate'),
        size: 120,
        cell: ({ row }) => dayjs(row.original.closeDate).format(USDateViewFormat),
      },
      {
        header: t`Status`,
        id: 'status',
        accessorKey: 'status',
        meta: { align: 'end' },
        enableSorting: sortFields.includes('status'),
        size: 120,
        filterComponent: (props) => <OpportunityStatusFilter {...props} />,
        cell: ({ row }) => <OpportunityStatusTag opportunity={row.original} />,
      },
      {
        header: t`Amount`,
        id: 'amount',
        accessorKey: 'amount',
        meta: { align: 'end' },
        enableSorting: sortFields.includes('amount'),
        size: 120,
        filterComponent: (props) => <RangeFilter {...props} />,
        cell: ({ row }) => numberFormat(row.original.amount, { isCurrency: true, precision: 0 }),
      },
    ],
    [sortFields],
  );

  return (
    <InstantSearchDataTable<OpportunityType>
      columns={columns}
      data={data}
      totalResults={totalResults}
      loading={isLoading}
      error={error}
      getRowId={(record) => record.id}
      emptyMessage={<Trans>No opportunities found.</Trans>}
      getFieldName={(fieldId: string) =>
        getOpportunityActivityFieldName(`o.${fieldId}` as ALL_OPPORTUNITY_ACTIVITY_FIELDS)
      }
      initialColumnPinning={{
        start: ['companyId'],
      }}
    />
  );
};
